<template>
  <main class="public-menu afro-bg">
    <section ref="menuExportRoot" class="menu-shell paper-panel" data-menu-export-root>
      <img class="leaf-left" :src="asset('left_leaf')" alt="" />
      <img class="leaf-right" :src="asset('right_leaf')" alt="" />

      <header class="poster-head">
        <div class="brand-mark">
          <img
            :src="asset('logo')"
            alt="Afrogato Cafe"
            role="button"
            title="Triple click para descargar el menu como imagen"
            @pointerup.prevent="handleLogoTap"
          />
        </div>

        <section
          v-if="currentFeature"
          class="feature-carousel"
          :class="{ paused: carouselPaused }"
          aria-label="Promociones y avisos"
          @mouseenter="pauseFeatureCarousel"
          @mouseleave="resumeFeatureCarousel"
        >
          <Transition name="feature-slide" mode="out-in">
            <article
              :key="currentFeature.id_token"
              class="feature-card carousel-card"
              :class="{ 'has-photo': Boolean(currentFeature.image_url) }"
            >
              <img v-if="currentFeature.image_url" :src="currentFeature.image_url" :alt="currentFeature.name" />
              <div class="feature-copy">
                <small>HOY EN AFROGATO</small>
                <h3>{{ currentFeature.name }}</h3>
                <p v-if="currentFeature.description">{{ currentFeature.description }}</p>
                <strong v-if="featurePrice(currentFeature)">{{ featurePrice(currentFeature) }}</strong>
              </div>
            </article>
          </Transition>

          <div class="carousel-status">
            <button type="button" aria-label="Promocion anterior" @click="changeFeature(-1)">&#8249;</button>
            <div class="carousel-progress" aria-hidden="true">
              <span
                :key="carouselCycleKey"
                :style="{ animationDuration: `${currentFeatureDuration}s` }"
              ></span>
            </div>
            <span>{{ activeFeatureIndex + 1 }} / {{ visibleFeatures.length }}</span>
            <button type="button" aria-label="Siguiente promocion" @click="changeFeature(1)">&#8250;</button>
          </div>

          <div v-if="visibleFeatures.length > 1" class="carousel-dots" aria-label="Seleccionar promocion">
            <button
              v-for="(feature, index) in visibleFeatures"
              :key="feature.id_token"
              type="button"
              :class="{ active: index === activeFeatureIndex }"
              :aria-label="`Ver promocion ${index + 1}`"
              :aria-current="index === activeFeatureIndex ? 'true' : undefined"
              @click="goToFeature(index)"
            ></button>
          </div>
        </section>

        <section v-else class="carousel-empty">
          <strong>Barra de cafe de especialidad</strong>
          <span>Bebidas, affogatos y mas</span>
        </section>

        <aside class="header-qr-card">
          <img :src="asset('qr')" alt="QR para abrir afrogato.app" />
          <div>
            <strong>Escanea el menu</strong>
            <a class="menu-qr-url" href="https://afrogato.app">https://afrogato.app</a>
          </div>
        </aside>

        <section v-if="visibleFeatures.length" class="feature-mobile-list" aria-label="Promociones y avisos">
          <article
            v-for="feature in visibleFeatures"
            :key="feature.id_token"
            class="feature-card"
            :class="{ 'has-photo': Boolean(feature.image_url) }"
          >
            <img v-if="feature.image_url" :src="feature.image_url" :alt="feature.name" />
            <div class="feature-copy">
              <h3>{{ feature.name }}</h3>
              <p v-if="feature.description">{{ feature.description }}</p>
              <strong v-if="featurePrice(feature)">{{ featurePrice(feature) }}</strong>
            </div>
          </article>
        </section>
      </header>

      <div class="menu-tools">
        <button class="afro-btn ghost share-mobile" type="button" @click="shareMenu">Compartir menu</button>
      </div>

      <div v-if="loading" class="state-card">Cargando menu...</div>
      <div v-else-if="error" class="state-card error">{{ error }}</div>

      <section v-else-if="search.trim()" class="search-results">
        <div class="results-head">
          <FlagLabel label="RESULTADOS" icon="coffee" />
          <button class="afro-btn ghost small" type="button" @click="search = ''">Limpiar</button>
        </div>
        <div v-if="filteredResults.length" class="results-grid">
          <article v-for="item in filteredResults" :key="item.id_token" class="result-card afro-section-card">
            <small>{{ item.category_name }}</small>
            <h3>{{ item.name }}</h3>
            <p v-if="item.description">{{ item.description }}</p>
            <div class="price-pills" :class="{ soldout: item.availability_status === 'sold_out' }">
              <span v-if="item.availability_status === 'sold_out'" class="search-soldout">Agotado</span>
              <template v-else>
              <span v-if="item.has_hot && item.price_hot !== null">Caliente {{ formatPrice(item.price_hot) }}</span>
              <span v-if="item.has_cold && item.price_cold !== null">Frio {{ formatPrice(item.price_cold) }}</span>
              <span v-if="item.price !== null">{{ formatPrice(item.price) }}</span>
              </template>
            </div>
          </article>
        </div>
        <p v-else class="empty-text">No encontramos productos con ese nombre.</p>
      </section>

      <template v-else>
        <section class="poster-grid">
          <div class="left-stack">
            <MenuCategoryBlock
              v-for="cat in leftCategories"
              :key="cat.id_token"
              :category="cat"
              :format-price="formatPrice"
            />
          </div>

          <div class="divider"></div>

          <div class="right-stack">
            <MenuCategoryBlock
              v-for="cat in rightCategories"
              :key="cat.id_token"
              :category="cat"
              :format-price="formatPrice"
              :asset-src="asset(cat.asset_key)"
            />

          </div>
        </section>

        <section v-if="fullCategories.length" class="full-width-menu">
          <MenuCategoryBlock
            v-for="cat in fullCategories"
            :key="cat.id_token"
            class="full-width-category afro-section-card"
            :category="cat"
            :format-price="formatPrice"
            :asset-src="asset(cat.asset_key)"
          />
        </section>

        <section v-if="crepesCategory || extrasCategory" class="crepes-extras-row">
          <CrepesMenuSection
            v-if="crepesCategory"
            :category="crepesCategory"
            :format-price="formatPrice"
          />

          <div v-if="extrasCategory" class="extras-card afro-section-card">
            <FlagLabel :label="extrasCategory.badge_label" :icon="extrasCategory.icon_key" />
            <div class="extras-list">
              <MenuRow
                v-for="product in extrasCategory.products"
                :key="product.id_token"
                :name="product.name"
                :price="product.price"
                :price-label="product.price_label"
                :status="product.availability_status"
              />
            </div>
          </div>
        </section>
      </template>

      <footer class="poster-footer">
        <div><span class="round-icon">&#9679;</span> Granos seleccionados</div>
        <div><span class="round-icon">&#9749;</span> Preparado al momento</div>
        <div><span class="round-icon">&#10022;</span> Calidad en cada detalle</div>
        <div><span class="round-icon">&#9825;</span> Hecho para ti</div>
      </footer>

      <section class="mobile-loyalty-cta">
        <strong>Registrate y acumula puntos para llevarte productos gratis.</strong>
        <router-link :to="mobileCtaPath">{{ mobileCtaLabel }}</router-link>
      </section>

      <section class="mobile-share-qr">
        <div class="mobile-qr-code">
          <img :src="asset('qr')" alt="QR para compartir el menu de Afrogato" />
          <a class="menu-qr-url" href="https://afrogato.app">https://afrogato.app</a>
        </div>
        <div>
          <strong>Comparte nuestro menu</strong>
          <span>Acerca esta pantalla para que otro dispositivo lo escanee.</span>
        </div>
      </section>
    </section>
  </main>

  <router-link v-if="isLoggedIn && $route.query.kiosco !== '1'" class="menu-wallet-float" to="/wallet" aria-label="Ir a mi wallet" title="Mi wallet"><svg viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="7" width="24" height="18" rx="4" /><path d="M4 13h24M8 20h6M21 19h3" /></svg></router-link>
  <MenuAnnouncement v-if="announcement" :announcement="announcement" @close="announcement = null" />
  <a class="mobile-whatsapp-float" :href="whatsappUrl" target="_blank" rel="noopener" aria-label="Pedir por WhatsApp">
    <span class="wa-icon" aria-hidden="true">
      <svg viewBox="0 0 32 32" focusable="false">
        <path d="M16.03 4.02c-6.62 0-12 5.26-12 11.74 0 2.07.56 4.08 1.62 5.85L4 28l6.63-1.58a12.35 12.35 0 0 0 5.4 1.25c6.62 0 12-5.26 12-11.74s-5.38-11.91-12-11.91Zm0 21.46c-1.77 0-3.5-.47-5.01-1.36l-.36-.21-3.93.94.96-3.76-.24-.39a9.23 9.23 0 0 1-1.43-4.94c0-5.25 4.49-9.55 10.01-9.55s10.01 4.3 10.01 9.55-4.49 9.72-10.01 9.72Zm5.5-7.2c-.3-.15-1.78-.86-2.06-.96-.28-.1-.48-.15-.68.15-.2.29-.78.95-.96 1.14-.18.2-.35.22-.65.07-.3-.15-1.27-.46-2.42-1.46-.9-.78-1.5-1.75-1.67-2.04-.18-.3-.02-.46.13-.6.14-.14.3-.35.45-.52.15-.17.2-.29.3-.49.1-.2.05-.37-.03-.52-.07-.15-.68-1.6-.93-2.19-.25-.58-.5-.5-.68-.51h-.58c-.2 0-.52.07-.8.37-.28.29-1.05 1-1.05 2.44s1.08 2.83 1.23 3.03c.15.2 2.13 3.17 5.17 4.44.72.3 1.29.49 1.73.62.73.23 1.39.2 1.91.12.58-.08 1.78-.71 2.03-1.4.25-.68.25-1.27.17-1.4-.07-.12-.27-.2-.57-.35Z" fill="currentColor"/>
      </svg>
    </span>
    <span v-if="showWhatsappTooltip" class="wa-tooltip">Puedes realizar tu pedido a domicilio por WhatsApp</span>
  </a>
  <div v-if="exportingImage" class="capture-toast">Generando imagen del menu...</div>
  <aside v-if="diagnostic" class="menu-diagnostic" aria-label="Diagnóstico del menú">
    <strong>Conexión del menú</strong><span>{{ socketState.connected ? 'WebSocket conectado' : 'WebSocket sin conexión' }}</span>
    <code>{{ diagnosticUrl }}</code><span v-if="socketState.reason">{{ socketState.reason }}</span>
    <span v-if="!socketState.connected">{{ fallbackState.checking ? 'Comprobando la web…' : 'Recarga de respaldo en ' + Math.ceil((fallbackState.dueIn || 0) / 60000) + ' min' }}</span>
    <span v-if="fallbackState.notice">{{ fallbackState.notice }}</span><span v-if="lastMenuSync">Menú actualizado: {{ lastMenuSync }}</span>
    <div><button @click="loadMenu(true)">Actualizar datos</button><button @click="diagnostic = false">Cerrar</button></div>
    <small>D abre/cierra este panel. La recarga de respaldo funciona tras 15 minutos sin WebSocket.</small>
  </aside>
</template>

<script>
import FlagLabel from '@/components/menu/FlagLabel.vue'
import MenuAnnouncement from '@/components/menu/MenuAnnouncement.vue'
import MenuRow from '@/components/menu/MenuRow.vue'
import MenuCategoryBlock from '@/components/menu/MenuCategoryBlock.vue'
import CrepesMenuSection from '@/components/menu/CrepesMenuSection.vue'
import { getPublicMenu } from '@/services/menuApi'
import { connectMenuSocket, closeMenuSocket, subscribeMenuSocketState } from '@/services/menuSocket'
import { createMenuReloadFallback } from '@/services/menuRealtimePolicy'

export default {
  name: 'PublicMenu',
  components: { FlagLabel, MenuRow, MenuCategoryBlock, CrepesMenuSection, MenuAnnouncement },
  data() {
    return {
      loading: true,
      error: '',
      settings: null, announcement: null,
      categories: [],
      features: [],
      search: '',
      reloadTimer: null,
      logoTapCount: 0,
      logoTapTimer: null,
      exportingImage: false,
      showWhatsappTooltip: false,
      whatsappTooltipTimer: null,
      whatsappHideTimer: null,
      activeFeatureIndex: 0,
      carouselTimer: null,
      carouselCycleKey: 0,
      carouselPaused: false,
      socketListener: null,
      diagnostic: false, socketState: {}, fallbackState: {}, lastMenuSync: '', fallbackTimer: null
    }
  },
  computed: {
    diagnosticUrl() { try { const url = new URL(this.socketState.url); return url.origin + url.pathname } catch (_e) { return 'Consultando dirección WebSocket…' } },
    leftCategories() {
      return this.categories.filter((cat) => cat.section_key === 'left')
    },
    rightCategories() {
      return this.categories.filter((cat) => cat.section_key === 'right')
    },
    extrasCategory() {
      return this.categories.find((cat) => cat.section_key === 'bottom')
    },
    crepesCategory() {
      return this.categories.find((cat) => cat.section_key === 'crepes')
    },
    fullCategories() {
      return this.categories.filter((cat) => cat.section_key === 'full')
    },
    visibleFeatures() {
      return (this.features || []).filter((item) => Number(item.active) === 1)
    },
    currentFeature() {
      if (!this.visibleFeatures.length) return null
      return this.visibleFeatures[this.activeFeatureIndex % this.visibleFeatures.length]
    },
    currentFeatureDuration() {
      const seconds = Number(this.currentFeature && this.currentFeature.display_duration_seconds)
      return Math.max(3, Math.min(60, seconds || 8))
    },
    isLoggedIn() {
      return Boolean(this.$store && this.$store.state && this.$store.state.user && this.$store.state.user.token)
    },
    mobileCtaLabel() {
      return this.isLoggedIn ? 'Ir a mi wallet' : 'Iniciar sesion / Registrarme'
    },
    mobileCtaPath() {
      return this.isLoggedIn ? '/wallet' : '/login'
    },
    whatsappUrl() {
      return 'https://wa.me/5215571107484?text=' + encodeURIComponent('Hola, me gustaria hacer un pedido')
    },
    filteredResults() {
      const q = this.search.trim().toLowerCase()
      if (!q) return []
      return this.categories.flatMap((cat) => (cat.products || []).map((product) => ({
        ...product,
        category_name: cat.name,
        layout_type: cat.layout_type
      }))).filter((item) => {
        return [item.name, item.description, item.category_name].filter(Boolean).join(' ').toLowerCase().includes(q)
      })
    }
  },
  created() {
    this.loadMenu()
    this.socketListener = (payload) => {
      if (payload?.event === 'loyalty.updated') return
      if (payload?.event?.startsWith('promomenu.')) return
      clearTimeout(this.reloadTimer)
      this.reloadTimer = setTimeout(() => this.loadMenu(true), 350)
    }
    connectMenuSocket(this.socketListener)
  },
  mounted() {
    this.diagnostic = this.$route.query.diagnostico === '1'
    this._reloadFallback = createMenuReloadFallback({ probe: this.probeMenu, reload: () => window.location.reload() })
    this._socketStateOff = subscribeMenuSocketState(state => { this.socketState = state; this._reloadFallback.status(state); this.fallbackState = this._reloadFallback.snapshot() })
    this.fallbackTimer = setInterval(this.fallbackTick, 10000)
    window.addEventListener('online', this.recoverMenu); window.addEventListener('keydown', this.diagnosticKey)
    this.whatsappTooltipTimer = setTimeout(() => {
      this.showWhatsappTooltip = true
      this.whatsappHideTimer = setTimeout(() => { this.showWhatsappTooltip = false }, 10000)
    }, 20000)
  },
  beforeUnmount() {
    this._disposed = true; this._reloadFallback?.stop(); this._socketStateOff?.(); clearInterval(this.fallbackTimer)
    window.removeEventListener('online', this.recoverMenu); window.removeEventListener('keydown', this.diagnosticKey)
    clearTimeout(this.reloadTimer)
    clearTimeout(this.logoTapTimer)
    clearTimeout(this.whatsappTooltipTimer)
    clearTimeout(this.whatsappHideTimer)
    clearTimeout(this.carouselTimer)
    closeMenuSocket(this.socketListener)
  },
  methods: {
    diagnosticKey(event) { if (event.key.toLowerCase() === 'd' && !event.ctrlKey && !event.altKey && !event.metaKey && !['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName)) this.diagnostic = !this.diagnostic },
    async probeMenu() {
      const controller = new AbortController(); const timer = setTimeout(() => controller.abort(), 8000)
      try { const response = await fetch(`/api/menu?_recovery=${Date.now()}`, { cache: 'no-store', headers: { Accept: 'application/json' }, signal: controller.signal }); const data = response.ok ? await response.json() : null; return Boolean(data && data.success !== false && Array.isArray(data.categories)) }
      finally { clearTimeout(timer) }
    },
    async fallbackTick() { await this._reloadFallback?.tick(); if (!this._disposed && this._reloadFallback) this.fallbackState = this._reloadFallback.snapshot() },
    recoverMenu() { this.loadMenu(true); this.fallbackTick() },
    async loadMenu(silent = false) {
      if (!silent) this.loading = true
      this.error = ''
      try {
        const response = await getPublicMenu()
        if (this._disposed) return
        this.settings = response.settings || {}
        this.categories = response.categories || []
        this.features = response.features || []
        if (!this._announcementConsidered) {
          this._announcementConsidered = true
          const ad = response.announcement
          if (ad?.enabled && ad.image_url && this.$route.query.kiosco !== '1' && (ad.audience !== 'mobile' || window.innerWidth <= 760)) this.announcement = ad
        }
        this.lastMenuSync = new Date().toLocaleTimeString('es-MX')
        this.resetFeatureCarousel()
      } catch (error) {
        if (!silent || !this.settings) this.error = 'No se pudo cargar el menu.'
      } finally {
        this.loading = false
      }
    },
    asset(key) {
      if (!key) return ''
      return (this.settings && this.settings.assets && this.settings.assets[key]) || `/assets/afrogato/${key}.png`
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return '-'
      const number = Number(value)
      if (Number.isNaN(number)) return value
      return `$${number.toFixed(number % 1 === 0 ? 0 : 2)}`
    },
    featurePrice(feature) {
      if (feature.price_label) return feature.price_label
      if (feature.price === null || feature.price === undefined || feature.price === '') return ''
      return this.formatPrice(feature.price)
    },
    resetFeatureCarousel() {
      clearTimeout(this.carouselTimer)
      if (this.activeFeatureIndex >= this.visibleFeatures.length) this.activeFeatureIndex = 0
      this.carouselCycleKey += 1
      if (!this.carouselPaused && this.visibleFeatures.length > 1) {
        this.carouselTimer = setTimeout(() => this.changeFeature(1), this.currentFeatureDuration * 1000)
      }
    },
    changeFeature(direction) {
      if (!this.visibleFeatures.length) return
      const count = this.visibleFeatures.length
      this.activeFeatureIndex = (this.activeFeatureIndex + direction + count) % count
      this.resetFeatureCarousel()
    },
    goToFeature(index) {
      this.activeFeatureIndex = index
      this.resetFeatureCarousel()
    },
    pauseFeatureCarousel() {
      this.carouselPaused = true
      clearTimeout(this.carouselTimer)
    },
    resumeFeatureCarousel() {
      this.carouselPaused = false
      this.resetFeatureCarousel()
    },
    handleLogoTap() {
      if (this.exportingImage) return
      clearTimeout(this.logoTapTimer)
      this.logoTapCount += 1
      if (this.logoTapCount >= 3) {
        this.logoTapCount = 0
        this.downloadMenuImage()
        return
      }
      this.logoTapTimer = setTimeout(() => {
        this.logoTapCount = 0
      }, 850)
    },
    async downloadMenuImage() {
      if (this.exportingImage) return
      this.exportingImage = true
      try {
        const canvas = await this.drawMenuPosterCanvas()
        await this.saveCanvasAsPng(canvas)
      } catch (error) {
        alert('No se pudo generar la imagen. Si usaste una foto externa, prueba subirla al sistema para evitar bloqueo del navegador.')
      } finally {
        this.exportingImage = false
      }
    },
    async drawMenuPosterCanvas() {
      if (document.fonts && document.fonts.ready) {
        try { await document.fonts.ready } catch (error) { /* continuar con fuentes del sistema */ }
      }

      const width = 1080
      const height = this.estimatePosterHeight()
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')

      const palette = {
        dark: '#26331f',
        dark2: '#1b2717',
        ink: '#192012',
        paper: '#efd7ad',
        paper2: '#f5dfba',
        gold: '#d8a64c',
        muted: '#7f6a4d',
        line: 'rgba(60, 54, 37, .42)'
      }

      const assets = await this.loadPosterAssets()

      ctx.fillStyle = palette.dark2
      ctx.fillRect(0, 0, width, height)

      this.roundRect(ctx, 8, 8, width - 16, height - 16, 30)
      ctx.fillStyle = palette.paper
      ctx.fill()
      ctx.lineWidth = 1.2
      ctx.strokeStyle = 'rgba(38, 51, 31, .85)'
      ctx.stroke()

      ctx.save()
      this.roundRect(ctx, 8, 8, width - 16, height - 16, 30)
      ctx.clip()

      const bg = ctx.createLinearGradient(0, 0, width, height)
      bg.addColorStop(0, '#f4ddb5')
      bg.addColorStop(.55, '#ecd0a3')
      bg.addColorStop(1, '#f2dcb8')
      ctx.fillStyle = bg
      ctx.fillRect(8, 8, width - 16, height - 16)

      ctx.save()
      ctx.setLineDash([3, 3])
      ctx.lineWidth = 1
      ctx.strokeStyle = 'rgba(38, 51, 31, .38)'
      this.roundRect(ctx, 18, 18, width - 36, height - 36, 22)
      ctx.stroke()
      ctx.restore()

      if (assets.left_leaf) this.drawImageContain(ctx, assets.left_leaf, 26, 60, 150, 92)
      if (assets.right_leaf) this.drawImageContain(ctx, assets.right_leaf, 845, 126, 260, 260)
      if (assets.logo) this.drawImageContain(ctx, assets.logo, 34, 24, 230, 285)

      if (this.currentFeature) {
        this.drawFeatureCardCanvas(
          ctx,
          this.currentFeature,
          { x: 286, y: 48, w: 508, h: 220 },
          palette,
          assets.features[this.currentFeature.id_token]
        )
      } else {
        this.roundRect(ctx, 286, 48, 508, 220, 18)
        ctx.fillStyle = palette.dark
        ctx.fill()
        ctx.fillStyle = '#f6dfb9'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        this.canvasFont(ctx, 900, 28, 'Georgia, Times New Roman, serif')
        ctx.fillText('BARRA DE CAFE DE ESPECIALIDAD', 540, 143)
        this.canvasFont(ctx, 800, 16, 'Montserrat, Arial, sans-serif')
        ctx.fillText('Bebidas, affogatos y mas', 540, 181)
      }

      const qrGradient = ctx.createLinearGradient(816, 48, 816, 268)
      qrGradient.addColorStop(0, '#344329')
      qrGradient.addColorStop(1, '#24311e')
      this.roundRect(ctx, 816, 48, 224, 220, 18)
      ctx.fillStyle = qrGradient
      ctx.fill()
      if (assets.qr) this.drawImageContain(ctx, assets.qr, 858, 66, 140, 140)
      ctx.fillStyle = '#f3dfbd'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      this.canvasFont(ctx, 900, 13, 'Montserrat, Arial, sans-serif')
      ctx.fillText('ESCANEA EL MENU', 928, 224)
      this.canvasFont(ctx, 800, 14, 'Montserrat, Arial, sans-serif')
      ctx.fillText('https://afrogato.app', 928, 246)

      const mainMenuBottom = this.drawMainMenuCanvas(ctx, palette, assets)
      const footerBottom = this.drawFooterCanvas(ctx, palette, mainMenuBottom + 28)

      ctx.restore()

      const output = document.createElement('canvas')
      output.width = width
      output.height = Math.min(height, footerBottom)
      const outputContext = output.getContext('2d')
      outputContext.drawImage(canvas, 0, 0)
      outputContext.strokeStyle = 'rgba(38, 51, 31, .85)'
      outputContext.lineWidth = 1.2
      this.roundRect(outputContext, 8, 8, width - 16, output.height - 16, 30)
      outputContext.stroke()
      return output
    },
    estimatePosterHeight() {
      const categoryHeight = (cat, full = false) => {
        const count = (cat.products || []).length
        const columns = full && Number(cat.product_columns) === 2 ? 2 : 1
        const rows = Math.ceil(count / columns)
        const rowHeight = cat.layout_type === 'beverages_dual' && columns === 1 ? 25 : 34
        return 94 + rows * rowHeight
      }
      const stackHeight = (items) => items.reduce((sum, cat) => sum + categoryHeight(cat) + 28, 0)
      const mainHeight = Math.max(stackHeight(this.leftCategories), stackHeight(this.rightCategories), 600)
      const fullHeight = this.fullCategories.reduce((sum, cat) => sum + categoryHeight(cat, true) + 28, 0)
      const lowerHeight = this.crepesCategory || this.extrasCategory ? 330 : 0
      return Math.max(1500, 340 + mainHeight + fullHeight + lowerHeight + 220)
    },
    async loadPosterAssets() {
      const baseAssets = {
        logo: this.asset('logo'),
        left_leaf: this.asset('left_leaf'),
        right_leaf: this.asset('right_leaf'),
        qr: this.asset('qr'),
        dessert: this.asset('dessert'),
        dog: this.asset('dog')
      }
      const entries = Object.entries(baseAssets)
      const loaded = {}
      await Promise.all(entries.map(async ([key, src]) => {
        loaded[key] = await this.loadCanvasImage(src)
      }))

      loaded.features = {}
      await Promise.all(this.visibleFeatures.map(async (feature) => {
        if (feature.image_url) loaded.features[feature.id_token] = await this.loadCanvasImage(feature.image_url)
      }))
      return loaded
    },
    loadCanvasImage(src) {
      if (!src) return Promise.resolve(null)
      return new Promise((resolve) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => resolve(img)
        img.onerror = () => resolve(null)
        img.src = src
      })
    },
    drawHeaderTexts(ctx, palette) {
      ctx.fillStyle = palette.ink
      ctx.textAlign = 'center'
      ctx.textBaseline = 'top'
      this.canvasFont(ctx, 900, 20, 'Montserrat, Arial, sans-serif')
      ctx.letterSpacing = '2px'
      ctx.fillText('BARRA DE CAFE', 160, 132)
      ctx.fillText('DE ESPECIALIDAD', 160, 168)
      ctx.fillText('BEBIDAS • AFFOGATOS', 845, 132)
      ctx.fillText('Y MAS', 845, 168)
      ctx.letterSpacing = '0px'
      ctx.strokeStyle = palette.ink
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(795, 225)
      ctx.lineTo(835, 225)
      ctx.moveTo(855, 225)
      ctx.lineTo(895, 225)
      ctx.stroke()
      ctx.save()
      ctx.translate(845, 225)
      ctx.rotate(.35)
      ctx.fillStyle = palette.ink
      ctx.beginPath()
      ctx.ellipse(0, 0, 8, 13, 0, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()
    },
    drawMainMenuCanvas(ctx, palette, assets) {
      const leftX = 60
      const rightX = 580
      const leftW = 462
      const rightW = 440
      const startY = 330
      const leftCategories = this.leftCategories
      let y = startY
      leftCategories.forEach((cat, index) => {
        y = this.drawCategoryCanvas(ctx, cat, leftX, y, leftW, palette, null)
        if (index < leftCategories.length - 1) {
          y += 18
          this.drawHorizontalRule(ctx, leftX, y, leftW, palette)
          y += 28
        }
      })
      const leftBottom = y

      let ry = startY
      this.rightCategories.forEach((cat, index) => {
        const sideImage = cat.asset_key && assets[cat.asset_key] ? assets[cat.asset_key] : null
        ry = this.drawCategoryCanvas(ctx, cat, rightX, ry, rightW, palette, sideImage)
        if ((cat.slug || '').includes('perruno')) {
          this.canvasFont(ctx, 400, 19, 'Comic Sans MS, Segoe Print, cursive')
          ctx.fillStyle = palette.dark
          ctx.textAlign = 'center'
          ctx.textBaseline = 'top'
          ctx.fillText('Para nuestros mejores amigos ♡', rightX + rightW / 2, ry + 4)
          ry += 44
        }
        if (index < this.rightCategories.length - 1) {
          ry += 18
          this.drawHorizontalRule(ctx, rightX, ry, rightW, palette)
          ry += 28
        }
      })

      const mainBottom = Math.max(leftBottom, ry)

      ctx.strokeStyle = 'rgba(148, 94, 29, .62)'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(550, 342)
      ctx.lineTo(550, mainBottom)
      ctx.stroke()

      let fullY = mainBottom + 38
      this.fullCategories.forEach((cat) => {
        fullY = this.drawFullCategoryCanvas(ctx, cat, 60, fullY, 960, palette)
        fullY += 30
      })

      const regularBottom = this.fullCategories.length ? fullY : mainBottom
      return this.crepesCategory || this.extrasCategory
        ? this.drawCrepesExtrasCanvas(ctx, 60, regularBottom + 16, 960, palette)
        : regularBottom
    },
    drawFullCategoryCanvas(ctx, cat, x, y, w, palette) {
      const products = cat.products || []
      const columns = Number(cat.product_columns) === 2 ? 2 : 1
      const startY = y
      this.drawFlagCanvas(ctx, x, y, Math.min(310, w * .36), 48, cat.badge_label || cat.name, cat.icon_key, palette)
      y += 74

      if (columns === 1) {
        products.forEach((product) => {
          y += this.drawSimpleRowCanvas(ctx, product, x + 8, y, w - 16, palette, 17)
        })
        return y + 10
      }

      const gap = 48
      const colW = (w - gap) / 2
      const splitAt = Math.ceil(products.length / 2)
      const lists = [products.slice(0, splitAt), products.slice(splitAt)]
      let maxBottom = y
      lists.forEach((list, column) => {
        let rowY = y
        list.forEach((product) => {
          rowY += this.drawSimpleRowCanvas(ctx, product, x + column * (colW + gap), rowY, colW, palette, 17)
        })
        maxBottom = Math.max(maxBottom, rowY)
      })

      ctx.save()
      ctx.setLineDash([3, 4])
      ctx.strokeStyle = 'rgba(148, 94, 29, .42)'
      ctx.beginPath()
      ctx.moveTo(x + w / 2, startY + 70)
      ctx.lineTo(x + w / 2, maxBottom)
      ctx.stroke()
      ctx.restore()
      return maxBottom + 12
    },
    drawCategoryCanvas(ctx, cat, x, y, w, palette, sideImage) {
      const products = cat.products || []
      const layout = cat.layout_type || 'single_price'
      this.drawFlagCanvas(ctx, x, y, Math.min(250, w * .48), 48, cat.badge_label || cat.name, cat.icon_key, palette)
      y += 72

      if (layout === 'beverages_dual') {
        const nameW = 185
        const priceW = 62
        const gapW = 70
        this.canvasFont(ctx, 900, 14, 'Montserrat, Arial, sans-serif')
        ctx.fillStyle = palette.ink
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText('CALIENTE', x + nameW + gapW + priceW / 2, y - 12)
        ctx.fillText('-', x + nameW + gapW + priceW + 43, y - 12)
        ctx.fillText('FRIO', x + w - priceW / 2, y - 12)

        products.forEach((product) => {
          this.drawDualRowCanvas(ctx, product, x, y, w, palette)
          y += 23
        })
        return y + 6
      }

      const imageSpace = sideImage ? 145 : 0
      const listW = w - imageSpace - (sideImage ? 14 : 0)
      products.forEach((product) => {
        const rowHeight = this.drawSimpleRowCanvas(ctx, product, x, y, listW, palette, 16)
        y += rowHeight
      })

      if (sideImage) {
        this.drawImageContain(ctx, sideImage, x + w - 150, y - Math.min(150, products.length * 28 + 20), 145, 145)
      }
      return y + 8
    },
    drawCrepesExtrasCanvas(ctx, x, y, w, palette) {
      const gap = 18
      const h = 300
      const hasCrepes = Boolean(this.crepesCategory)
      const hasExtras = Boolean(this.extrasCategory)
      const crepesW = hasCrepes && hasExtras ? Math.round((w - gap) * .75) : (hasCrepes ? w : 0)
      const extrasX = x + crepesW + (hasCrepes && hasExtras ? gap : 0)
      const extrasW = hasExtras ? w - (extrasX - x) : 0

      if (hasCrepes) {
        this.roundRect(ctx, x, y, crepesW, h, 16)
        ctx.fillStyle = 'rgba(245, 223, 186, .5)'
        ctx.fill()
        ctx.strokeStyle = 'rgba(60, 54, 37, .24)'
        ctx.stroke()
        this.drawFlagCanvas(ctx, x + 16, y + 14, Math.min(190, crepesW * .32), 40, this.crepesCategory.badge_label || 'CREPAS', this.crepesCategory.icon_key, palette, 16)

        const products = this.crepesCategory.products || []
        const innerX = x + 14
        const innerY = y + 68
        const innerW = crepesW - 28
        const innerH = h - 82
        const productGap = 12
        const wideW = products.length > 1 ? Math.round((innerW - productGap) * .7) : innerW
        products.slice(0, 2).forEach((product, index) => {
          const productX = index === 0 ? innerX : innerX + wideW + productGap
          const productW = index === 0 ? wideW : innerW - wideW - productGap
          this.drawCrepeProductCanvas(ctx, product, productX, innerY, productW, innerH, palette, index === 0)
        })
      }

      if (hasExtras) {
        this.roundRect(ctx, extrasX, y, extrasW, h, 16)
        ctx.fillStyle = 'rgba(245, 223, 186, .5)'
        ctx.fill()
        ctx.strokeStyle = 'rgba(60, 54, 37, .24)'
        ctx.stroke()
        this.drawFlagCanvas(ctx, extrasX + 16, y + 14, Math.min(150, extrasW - 50), 40, this.extrasCategory.badge_label || 'EXTRAS', this.extrasCategory.icon_key, palette, 15)
        let rowY = y + 86
        ;(this.extrasCategory.products || []).slice(0, 8).forEach((product) => {
          rowY += this.drawSimpleRowCanvas(ctx, product, extrasX + 16, rowY, extrasW - 32, palette, 13)
        })
      }

      return y + h
    },
    drawCrepeProductCanvas(ctx, product, x, y, w, h, palette, wide = false) {
      this.roundRect(ctx, x, y, w, h, 12)
      ctx.fillStyle = 'rgba(255, 249, 235, .22)'
      ctx.fill()
      ctx.save()
      ctx.setLineDash([4, 4])
      ctx.strokeStyle = 'rgba(38, 51, 31, .3)'
      ctx.stroke()
      ctx.restore()

      const soldOut = product.availability_status === 'sold_out'
      ctx.textAlign = 'left'
      ctx.textBaseline = 'middle'
      ctx.fillStyle = soldOut ? 'rgba(38, 51, 31, .48)' : palette.dark
      this.canvasFont(ctx, 900, wide ? 18 : 16, 'Georgia, Times New Roman, serif')
      const title = this.wrapCanvasText(ctx, product.name || '', Math.max(70, w - 90), 1)[0]
      ctx.fillText(title, x + 12, y + 20)

      const price = soldOut ? 'AGOTADA' : (product.price_label || `Desde ${this.formatPrice(product.price)}`)
      ctx.textAlign = 'right'
      ctx.fillStyle = soldOut ? '#b95f57' : palette.dark
      this.canvasFont(ctx, 900, wide ? 12 : 10, 'Montserrat, Arial, sans-serif')
      ctx.fillText(price, x + w - 10, y + 20)

      const rule = product.customization_rule || {}
      const included = Number(rule.included_count || 0)
      const includedText = included === 1 ? '1 ingrediente incluido' : `${included} ingredientes incluidos`
      ctx.textAlign = 'left'
      ctx.fillStyle = '#755624'
      this.canvasFont(ctx, 900, wide ? 10 : 9, 'Montserrat, Arial, sans-serif')
      ctx.fillText(`${includedText}  •  +${this.formatPrice(rule.extra_price || 0)} cada extra`, x + 12, y + 46)

      const groups = product.customization_groups || []
      const groupGap = wide ? 9 : 0
      let widths = [w - 24]
      if (wide && groups.length === 3) {
        const available = w - 24 - groupGap * 2
        widths = [available * .2, available * .29, available * .51]
      } else if (wide && groups.length > 1) {
        const available = w - 24 - groupGap * (groups.length - 1)
        widths = groups.map(() => available / groups.length)
      }
      let groupX = x + 12
      groups.forEach((group, index) => {
        const groupW = widths[index] || widths[0]
        this.drawIngredientGroupCanvas(ctx, group, groupX, y + 66, groupW, palette)
        groupX += groupW + groupGap
      })
    },
    drawIngredientGroupCanvas(ctx, group, x, y, w, palette) {
      ctx.textAlign = 'left'
      ctx.textBaseline = 'middle'
      ctx.fillStyle = palette.dark
      this.canvasFont(ctx, 900, 10, 'Montserrat, Arial, sans-serif')
      ctx.fillText(String(group.name || '').toUpperCase(), x, y)

      const options = group.options || []
      const columns = options.length > 7 ? 2 : 1
      const rows = Math.ceil(options.length / columns)
      const columnGap = columns > 1 ? 7 : 0
      const columnW = (w - columnGap * (columns - 1)) / columns
      options.forEach((option, index) => {
        const column = Math.floor(index / rows)
        const row = index % rows
        const optionX = x + column * (columnW + columnGap)
        const optionY = y + 18 + row * 16
        const unavailable = option.availability_status === 'sold_out'
        ctx.fillStyle = unavailable ? '#a7443d' : palette.ink
        this.canvasFont(ctx, unavailable ? 800 : 500, 9, 'Arial, sans-serif')
        const label = `${unavailable ? '×' : '•'} ${option.name || ''}`
        ctx.fillText(this.wrapCanvasText(ctx, label, columnW - 2, 1)[0], optionX, optionY)
      })
    },
    drawBottomExtrasCanvas(ctx, x, y, w, palette, assets) {
      const extrasW = 265
      const qrW = 178
      const h = 188
      if (this.extrasCategory) {
        this.roundRect(ctx, x, y, extrasW, h, 16)
        ctx.fillStyle = 'rgba(245, 223, 186, .5)'
        ctx.fill()
        ctx.strokeStyle = 'rgba(60, 54, 37, .24)'
        ctx.stroke()
        this.drawFlagCanvas(ctx, x + 52, y + 20, 145, 40, this.extrasCategory.badge_label || 'EXTRAS', this.extrasCategory.icon_key, palette, 16)
        let rowY = y + 86
        const extrasProducts = (this.extrasCategory.products || []).slice(0, 5)
        const extrasFont = extrasProducts.length > 4 ? 13 : 14
        const extrasStep = extrasProducts.length > 4 ? 21 : 24
        extrasProducts.forEach((product) => {
          this.drawSimpleRowCanvas(ctx, product, x + 18, rowY, extrasW - 36, palette, extrasFont)
          rowY += extrasStep
        })
      }

      const qx = x + w - qrW
      const qrGradient = ctx.createLinearGradient(qx, y, qx, y + h)
      qrGradient.addColorStop(0, '#344329')
      qrGradient.addColorStop(1, '#24311e')
      this.roundRect(ctx, qx, y, qrW, h, 12)
      ctx.fillStyle = qrGradient
      ctx.fill()
      if (assets.qr) this.drawImageContain(ctx, assets.qr, qx + 29, y + 24, 120, 120)
      this.canvasFont(ctx, 900, 13, 'Montserrat, Arial, sans-serif')
      ctx.fillStyle = '#f3dfbd'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'top'
      ctx.fillText('Mira el menu en tu', qx + qrW / 2, y + 148)
      ctx.fillText('celular :D', qx + qrW / 2, y + 164)
      return y + h
    },
    drawFeatureStripCanvas(ctx, palette, assets, topY) {
      if (!this.visibleFeatures.length) return topY
      const x = 52
      const y = topY
      const w = 976
      const gap = 16
      const cardHeight = 220
      const rows = Math.ceil(this.visibleFeatures.length / 2)
      const h = 112 + rows * cardHeight + Math.max(0, rows - 1) * gap + 24
      this.roundRect(ctx, x, y, w, h, 18)
      ctx.fillStyle = 'rgba(245, 223, 186, .46)'
      ctx.fill()
      ctx.strokeStyle = 'rgba(60, 54, 37, .22)'
      ctx.lineWidth = 1
      ctx.stroke()

      const hx = x + 22
      const hy = y + 22
      const hw = w - 44
      const hh = 62
      this.roundRect(ctx, hx, hy, hw, hh, 14)
      ctx.strokeStyle = 'rgba(38, 51, 31, .35)'
      ctx.setLineDash([3, 3])
      ctx.stroke()
      ctx.setLineDash([])

      ctx.fillStyle = palette.dark
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      this.canvasFont(ctx, 900, 34, 'Georgia, Times New Roman, serif')
      ctx.fillText('HOY EN AFROGATO', hx + hw / 2 - 70, hy + hh / 2)
      this.canvasFont(ctx, 900, 13, 'Montserrat, Arial, sans-serif')
      ctx.fillText('PROMOCIONES, NOVEDADES Y FAVORITOS', hx + hw / 2 + 230, hy + hh / 2 + 1)

      const gridX = x + 22
      const gridY = y + 108
      const gridW = w - 44
      const features = this.visibleFeatures
      const cardWidth = (gridW - gap) / 2
      const rects = features.map((feature, index) => ({
        x: gridX + (index % 2) * (cardWidth + gap),
        y: gridY + Math.floor(index / 2) * (cardHeight + gap),
        w: cardWidth,
        h: cardHeight
      }))
      features.forEach((feature, index) => {
        this.drawFeatureCardCanvas(ctx, feature, rects[index], palette, assets.features[feature.id_token])
      })
      return y + h
    },
    getFeatureRects(features, x, y, w, h) {
      const gap = 14
      const count = features.length
      if (count <= 1) return [{ x, y, w, h }]
      if (count === 2) return [
        { x, y, w: (w - gap) / 2, h },
        { x: x + (w + gap) / 2, y, w: (w - gap) / 2, h }
      ]
      if (count === 3 && features.some((f) => f.layout_span === 'wide')) {
        const leftW = Math.floor((w - gap) * .52)
        const rightW = w - gap - leftW
        return [
          { x, y, w: leftW, h },
          { x: x + leftW + gap, y, w: rightW, h: (h - gap) / 2 },
          { x: x + leftW + gap, y: y + (h + gap) / 2, w: rightW, h: (h - gap) / 2 }
        ]
      }
      if (count === 3) {
        const cw = (w - gap * 2) / 3
        return [0, 1, 2].map((i) => ({ x: x + i * (cw + gap), y, w: cw, h }))
      }
      if (count === 4) {
        const cw = (w - gap) / 2
        const ch = (h - gap) / 2
        return [0, 1, 2, 3].map((i) => ({ x: x + (i % 2) * (cw + gap), y: y + Math.floor(i / 2) * (ch + gap), w: cw, h: ch }))
      }
      if (count === 5) {
        const topW = (w - gap * 2) / 3
        const bottomW = (w - gap) / 2
        const rowH = (h - gap) / 2
        return [
          { x, y, w: topW, h: rowH },
          { x: x + topW + gap, y, w: topW, h: rowH },
          { x: x + (topW + gap) * 2, y, w: topW, h: rowH },
          { x, y: y + rowH + gap, w: bottomW, h: rowH },
          { x: x + bottomW + gap, y: y + rowH + gap, w: bottomW, h: rowH }
        ]
      }
      const cols = count <= 6 ? 3 : 4
      const rows = Math.ceil(Math.min(count, 8) / cols)
      const cw = (w - gap * (cols - 1)) / cols
      const ch = (h - gap * (rows - 1)) / rows
      return features.map((feature, i) => ({ x: x + (i % cols) * (cw + gap), y: y + Math.floor(i / cols) * (ch + gap), w: cw, h: ch }))
    },
    drawFeatureCardCanvas(ctx, feature, rect, palette, image) {
      if (!rect) return
      this.roundRect(ctx, rect.x, rect.y, rect.w, rect.h, 18)
      ctx.save()
      ctx.clip()
      const gradient = ctx.createLinearGradient(rect.x, rect.y, rect.x + rect.w, rect.y + rect.h)
      gradient.addColorStop(0, '#26331f')
      gradient.addColorStop(1, '#405034')
      ctx.fillStyle = gradient
      ctx.fillRect(rect.x, rect.y, rect.w, rect.h)
      if (image) {
        this.drawImageCover(ctx, image, rect.x, rect.y, rect.w, rect.h)
        ctx.fillStyle = 'rgba(23, 33, 18, .64)'
        ctx.fillRect(rect.x, rect.y, rect.w, rect.h)
      }
      ctx.restore()
      ctx.strokeStyle = 'rgba(255,255,255,.1)'
      ctx.lineWidth = 1
      this.roundRect(ctx, rect.x + .5, rect.y + .5, rect.w - 1, rect.h - 1, 18)
      ctx.stroke()

      const cx = rect.x + rect.w / 2
      const cy = rect.y + rect.h / 2
      const large = rect.w > 600 || rect.h > 300
      const titleSize = large ? 48 : rect.w < 250 ? 25 : 32
      const descSize = large ? 20 : rect.w < 250 ? 13 : 16
      const priceSize = large ? 40 : rect.w < 250 ? 22 : 28
      const title = feature.name || ''
      const desc = feature.description || ''
      const price = this.featurePrice(feature)

      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillStyle = '#f6dfb9'
      this.canvasFont(ctx, 900, titleSize, 'Georgia, Times New Roman, serif')
      const titleLines = this.wrapCanvasText(ctx, title, rect.w - 46, large ? 2 : 2)
      this.canvasFont(ctx, 800, descSize, 'Montserrat, Arial, sans-serif')
      const descLines = desc ? this.wrapCanvasText(ctx, desc, rect.w - 60, large ? 4 : (rect.h > 155 ? 3 : 2)) : []

      const totalTextH = titleLines.length * titleSize * 1.05 + descLines.length * descSize * 1.25 + (price ? priceSize * 1.15 : 0) + (descLines.length ? 10 : 0) + (price ? 14 : 0)
      let ty = cy - totalTextH / 2 + titleSize / 2
      ctx.fillStyle = '#f6dfb9'
      this.canvasFont(ctx, 900, titleSize, 'Georgia, Times New Roman, serif')
      titleLines.forEach((line) => {
        ctx.fillText(line, cx, ty)
        ty += titleSize * 1.05
      })
      if (descLines.length) {
        ty += 8
        ctx.fillStyle = 'rgba(246, 223, 185, .92)'
        this.canvasFont(ctx, 800, descSize, 'Montserrat, Arial, sans-serif')
        descLines.forEach((line) => {
          ctx.fillText(line, cx, ty)
          ty += descSize * 1.25
        })
      }
      if (price) {
        ty += 10
        ctx.fillStyle = '#f0bd55'
        this.canvasFont(ctx, 900, priceSize, 'Montserrat, Arial, sans-serif')
        ctx.fillText(price, cx, ty + priceSize * .2)
      }
    },
    drawFooterCanvas(ctx, palette, y) {
      const h = 92
      ctx.fillStyle = '#11130f'
      ctx.fillRect(0, y, 1080, h)
      const items = [
        ['•', 'GRANOS SELECCIONADOS'],
        ['☕', 'PREPARADO AL MOMENTO'],
        ['✦', 'CALIDAD EN CADA DETALLE'],
        ['♡', 'HECHO PARA TI']
      ]
      const colW = 1080 / 4
      items.forEach((item, i) => {
        const x = i * colW
        if (i > 0) {
          ctx.strokeStyle = palette.gold
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.moveTo(x, y + 25)
          ctx.lineTo(x, y + h - 25)
          ctx.stroke()
        }
        ctx.strokeStyle = palette.gold
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.arc(x + 64, y + h / 2, 18, 0, Math.PI * 2)
        ctx.stroke()
        ctx.fillStyle = palette.gold
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        this.canvasFont(ctx, 800, 13, 'Arial, sans-serif')
        ctx.fillText(item[0], x + 64, y + h / 2)
        ctx.textAlign = 'left'
        this.canvasFont(ctx, 800, 12, 'Montserrat, Arial, sans-serif')
        ctx.fillText(item[1], x + 95, y + h / 2 + 1)
      })
      return y + h
    },
    drawDualRowCanvas(ctx, product, x, y, w, palette) {
      const nameW = 185
      const hotX = x + 300
      const coldX = x + w - 34
      this.canvasFont(ctx, 500, 16, 'Arial, sans-serif')
      const soldOut = product.availability_status === 'sold_out'
      ctx.fillStyle = soldOut ? 'rgba(25, 32, 18, .48)' : palette.ink
      ctx.textAlign = 'left'
      ctx.textBaseline = 'middle'
      ctx.fillText(product.name || '', x, y)
      this.drawDottedLine(ctx, x + Math.min(nameW - 18, ctx.measureText(product.name || '').width + 12), y + 4, hotX - 45, y + 4, palette)
      if (soldOut) {
        this.drawSoldOutBadgeCanvas(ctx, hotX - 52, y - 10)
        return
      }
      this.canvasFont(ctx, 900, 16, 'Arial, sans-serif')
      ctx.textAlign = 'right'
      ctx.fillText(product.has_hot && product.price_hot !== null ? this.formatPrice(product.price_hot) : '-', hotX, y)
      this.drawDottedLine(ctx, hotX + 18, y + 4, coldX - 50, y + 4, palette)
      ctx.fillText(product.has_cold && product.price_cold !== null ? this.formatPrice(product.price_cold) : '-', coldX, y)
    },
    drawSimpleRowCanvas(ctx, product, x, y, w, palette, fontSize = 16) {
      const name = product.name || ''
      const price = product.price_label || (product.price !== null && product.price !== undefined ? this.formatPrice(product.price) : (product.price_cold !== null && product.price_cold !== undefined ? this.formatPrice(product.price_cold) : ''))
      this.canvasFont(ctx, 500, fontSize, 'Arial, sans-serif')
      const soldOut = product.availability_status === 'sold_out'
      ctx.fillStyle = soldOut ? 'rgba(25, 32, 18, .48)' : palette.ink
      ctx.textAlign = 'left'
      ctx.textBaseline = 'middle'
      const maxNameW = w - 92
      const lines = this.wrapCanvasText(ctx, name, maxNameW, 2)
      const lineH = fontSize * 1.12
      lines.forEach((line, index) => ctx.fillText(line, x, y + index * lineH))
      const lastLineWidth = ctx.measureText(lines[lines.length - 1] || '').width
      const lineY = y + (lines.length - 1) * lineH + 5
      this.drawDottedLine(ctx, x + Math.min(maxNameW - 10, lastLineWidth + 12), lineY, x + w - 68, lineY, palette)
      if (soldOut) {
        this.drawSoldOutBadgeCanvas(ctx, x + w - 74, y + (lines.length - 1) * lineH - 10)
      } else {
        this.canvasFont(ctx, 900, fontSize, 'Arial, sans-serif')
        ctx.textAlign = 'right'
        ctx.fillText(price, x + w, y + (lines.length - 1) * lineH)
      }
      return Math.max(23, lines.length * lineH + 3)
    },
    drawSoldOutBadgeCanvas(ctx, x, y) {
      this.roundRect(ctx, x, y, 74, 22, 11)
      ctx.fillStyle = '#b95f57'
      ctx.fill()
      ctx.fillStyle = '#fff8ef'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      this.canvasFont(ctx, 900, 10, 'Montserrat, Arial, sans-serif')
      ctx.fillText('AGOTADO', x + 37, y + 11)
    },
    drawFlagCanvas(ctx, x, y, w, h, label, icon, palette, fontSize = 19) {
      ctx.save()
      ctx.fillStyle = palette.dark
      ctx.shadowColor = 'rgba(0,0,0,.25)'
      ctx.shadowBlur = 10
      ctx.shadowOffsetY = 5
      ctx.beginPath()
      ctx.moveTo(x, y)
      ctx.lineTo(x + w, y)
      ctx.lineTo(x + w + 22, y + h / 2)
      ctx.lineTo(x + w, y + h)
      ctx.lineTo(x, y + h)
      ctx.closePath()
      ctx.fill()
      ctx.restore()
      ctx.fillStyle = '#f6dfb9'
      ctx.textBaseline = 'middle'
      ctx.textAlign = 'left'
      this.canvasFont(ctx, 900, fontSize, 'Georgia, Times New Roman, serif')
      const iconText = this.iconToText(icon)
      ctx.fillText(`${iconText}  ${String(label || '').toUpperCase()}`, x + 18, y + h / 2 + 1)
    },
    iconToText(icon) {
      const map = { snow: '❄', dessert: '🍰', paw: '🐾', star: '✦', cup: '☕', coffee: '•', sprout: '♦' }
      return map[icon] || '•'
    },
    drawHorizontalRule(ctx, x, y, w, palette) {
      ctx.save()
      ctx.setLineDash([3, 3])
      ctx.strokeStyle = palette.line
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(x, y)
      ctx.lineTo(x + w, y)
      ctx.stroke()
      ctx.restore()
    },
    drawDottedLine(ctx, x1, y1, x2, y2, palette) {
      if (x2 <= x1) return
      ctx.save()
      ctx.setLineDash([1, 4])
      ctx.lineWidth = 1.4
      ctx.strokeStyle = palette.line
      ctx.beginPath()
      ctx.moveTo(x1, y1)
      ctx.lineTo(x2, y2)
      ctx.stroke()
      ctx.restore()
    },
    wrapCanvasText(ctx, text, maxWidth, maxLines = 2) {
      const words = String(text || '').split(/\s+/).filter(Boolean)
      const lines = []
      let line = ''
      words.forEach((word) => {
        const test = line ? `${line} ${word}` : word
        if (ctx.measureText(test).width <= maxWidth || !line) {
          line = test
        } else {
          lines.push(line)
          line = word
        }
      })
      if (line) lines.push(line)
      if (lines.length > maxLines) {
        const trimmed = lines.slice(0, maxLines)
        let last = trimmed[maxLines - 1]
        while (last.length > 3 && ctx.measureText(last + '…').width > maxWidth) last = last.slice(0, -1)
        trimmed[maxLines - 1] = last + '…'
        return trimmed
      }
      return lines.length ? lines : ['']
    },
    drawImageContain(ctx, img, x, y, w, h) {
      if (!img) return
      const ratio = Math.min(w / img.naturalWidth, h / img.naturalHeight)
      const dw = img.naturalWidth * ratio
      const dh = img.naturalHeight * ratio
      ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh)
    },
    drawImageCover(ctx, img, x, y, w, h) {
      if (!img) return
      const ratio = Math.max(w / img.naturalWidth, h / img.naturalHeight)
      const sw = w / ratio
      const sh = h / ratio
      const sx = (img.naturalWidth - sw) / 2
      const sy = (img.naturalHeight - sh) / 2
      ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h)
    },
    roundRect(ctx, x, y, w, h, r) {
      const radius = Math.min(r, w / 2, h / 2)
      ctx.beginPath()
      ctx.moveTo(x + radius, y)
      ctx.lineTo(x + w - radius, y)
      ctx.quadraticCurveTo(x + w, y, x + w, y + radius)
      ctx.lineTo(x + w, y + h - radius)
      ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h)
      ctx.lineTo(x + radius, y + h)
      ctx.quadraticCurveTo(x, y + h, x, y + h - radius)
      ctx.lineTo(x, y + radius)
      ctx.quadraticCurveTo(x, y, x + radius, y)
      ctx.closePath()
    },
    canvasFont(ctx, weight, size, family) {
      ctx.font = `${weight} ${size}px ${family}`
    },
    saveCanvasAsPng(canvas) {
      const fileName = `menu-afrogato-${new Date().toISOString().slice(0, 10)}.png`
      return new Promise((resolve, reject) => {
        try {
          if (!canvas.toBlob) {
            const fallbackLink = document.createElement('a')
            fallbackLink.href = canvas.toDataURL('image/png')
            fallbackLink.download = fileName
            fallbackLink.click()
            resolve()
            return
          }

          canvas.toBlob((blob) => {
            if (!blob) {
              window.open(canvas.toDataURL('image/png'), '_blank')
              resolve()
              return
            }
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = fileName
            document.body.appendChild(link)
            link.click()
            link.remove()
            setTimeout(() => URL.revokeObjectURL(url), 1200)
            resolve()
          }, 'image/png', 1)
        } catch (error) {
          reject(error)
        }
      })
    },
    async shareMenu() {
      const url = (this.settings && this.settings.qr_url) || 'https://afrogato.app'
      const payload = {
        title: (this.settings && this.settings.share_title) || 'Menu Afrogato Cafe',
        text: (this.settings && this.settings.share_text) || 'Mira el menu de Afrogato Cafe',
        url
      }
      if (navigator.share) {
        await navigator.share(payload)
        return
      }
      if (navigator.clipboard) await navigator.clipboard.writeText(url)
      alert('Link copiado: ' + url)
    }
  }
}
</script>

<style scoped>
.menu-diagnostic { position: fixed; bottom: 16px; left: 16px; z-index: 100; width: min(430px, calc(100vw - 32px)); display: grid; gap: 8px; padding: 18px; background: #fff8e9; color: #26331f; border: 1px solid #26331f35; border-radius: 18px; box-shadow: 0 12px 40px #0004; text-align: left; font: 13px Arial, sans-serif; }
.menu-diagnostic code { word-break: break-all; color: #855919; }.menu-diagnostic > div { display: flex; gap: 8px; }.menu-diagnostic button { border: 1px solid #26331f35; border-radius: 12px; padding: 10px; background: #26331f; color: #fff8e9; cursor: pointer; }.menu-diagnostic small { font-size: 11px; line-height: 1.5; }
.public-menu {
  height: auto;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: clamp(6px, 1.15vh, 14px);
  overflow: auto;
}
.menu-shell {
  width: min(100%, 1100px);
  height: auto;
  min-height: calc(100dvh - clamp(12px, 2.3vh, 28px));
  position: relative;
  overflow: hidden;
  padding: clamp(12px, 1.7vh, 22px) clamp(18px, 3vw, 42px) 0;
  display: flex;
  flex-direction: column;
}
.menu-shell::before {
  content: '';
  position: absolute;
  inset: 8px;
  border: 1px dashed rgba(25, 32, 18, .42);
  pointer-events: none;
}

.public-menu.exporting-poster {
  width: 1080px;
  height: 1920px;
  min-height: 1920px;
  padding: 16px;
  overflow: hidden;
  align-items: stretch;
}
.public-menu.exporting-poster .menu-shell {
  width: 1048px;
  height: 1888px;
  min-height: 0;
  border-radius: 28px;
  padding: 18px 42px 0;
}
.public-menu.exporting-poster .poster-head {
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr);
  min-height: 292px;
}
.public-menu.exporting-poster .brand-mark img {
  width: 360px;
  max-height: 292px;
}
.public-menu.exporting-poster .headline {
  margin-top: 86px;
  font-size: 17px;
}
.public-menu.exporting-poster .poster-grid {
  grid-template-columns: minmax(0, 1.05fr) 1px minmax(0, 1fr);
  gap: 30px;
  flex: 0 1 auto;
}
.public-menu.exporting-poster .feature-strip {
  flex: 1 1 0;
  min-height: 260px;
  margin: 14px 0 12px;
}
.public-menu.exporting-poster .poster-footer {
  margin-left: -42px;
  margin-right: -42px;
}
.leaf-left,
.leaf-right {
  position: absolute;
  opacity: .78;
  pointer-events: none;
  z-index: 0;
}
.leaf-left { width: clamp(115px, 9vw, 145px); left: 22px; top: 42px; }
.leaf-right { width: clamp(185px, 16vw, 245px); right: -24px; top: 110px; }
.poster-head,
.poster-grid,
.full-width-menu,
.crepes-extras-row,
.mobile-share-qr,
.poster-footer,
.menu-tools,
.state-card,
.search-results,
.feature-strip {
  position: relative;
  z-index: 1;
}
.poster-head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr);
  align-items: stretch;
  gap: clamp(14px, 2vw, 24px);
  min-height: clamp(150px, 16vh, 170px);
  flex: 0 0 auto;
  margin-bottom: clamp(12px, 1.6vh, 20px);
}
.brand-mark { display: flex; justify-content: flex-start; align-items: center; min-width: 0; }
.brand-mark img {
  width: 100%;
  height: 100%;
  max-height: clamp(150px, 16vh, 170px);
  object-fit: contain;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  touch-action: manipulation;
}
.feature-carousel {
  position: relative;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border-radius: 22px;
  box-shadow: 0 16px 34px rgba(24,31,19,.18);
}
.carousel-card { width: 100%; height: 100%; min-height: 100%; border-radius: 22px; }
.carousel-card .feature-copy { padding: clamp(11px, 1.25vw, 17px) clamp(14px, 1.5vw, 20px) 42px; }
.carousel-card .feature-copy small { margin-bottom: 5px; color: #efbd59; font-size: clamp(9px, .78vw, 11px); font-weight: 1000; letter-spacing: .13em; }
.carousel-card .feature-copy h3 { font-size: clamp(19px, 1.8vw, 28px); }
.carousel-card .feature-copy p { font-size: clamp(10px, .9vw, 13px); -webkit-line-clamp: 2; }
.carousel-card .feature-copy strong { font-size: clamp(16px, 1.45vw, 22px); }
.carousel-card.has-photo { grid-template-columns: minmax(125px, 36%) 1fr; }
.carousel-card img { width: 100%; min-height: 100%; }
.header-qr-card {
  min-width: 0;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 8px;
  padding: clamp(10px, 1.25vw, 16px);
  border-radius: 22px;
  background: linear-gradient(180deg, #344329, #24311e);
  color: #f3dfbd;
  text-align: center;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.08), 0 12px 24px rgba(0,0,0,.16);
}
.header-qr-card img {
  width: min(100%, clamp(92px, 9vw, 125px));
  max-height: clamp(92px, 12vh, 130px);
  object-fit: contain;
  padding: 6px;
  border-radius: 7px;
  background: #f3dfbd;
}
.header-qr-card div { display: grid; gap: 2px; }
.header-qr-card strong { font-size: clamp(11px, 1vw, 14px); text-transform: uppercase; letter-spacing: .045em; }
.menu-qr-url { color: #efbd59; font-size: clamp(11px, 1.2vw, 14px); font-weight: 900; text-align: center; text-decoration: none; white-space: nowrap; }
.menu-qr-url:focus-visible { outline: 2px solid currentColor; outline-offset: 3px; border-radius: 2px; }
.carousel-status {
  position: absolute;
  z-index: 4;
  left: 14px;
  right: 14px;
  bottom: 11px;
  display: grid;
  grid-template-columns: 28px minmax(80px, 1fr) auto 28px;
  align-items: center;
  gap: 9px;
  color: #f6dfb9;
  font-size: 11px;
  font-weight: 900;
}
.carousel-status button { width: 28px; height: 28px; border: 1px solid rgba(246,223,185,.45); border-radius: 50%; background: rgba(18,23,15,.48); color: #f6dfb9; cursor: pointer; font-size: 22px; line-height: 1; }
.carousel-progress { height: 5px; overflow: hidden; border-radius: 999px; background: rgba(246,223,185,.22); }
.carousel-progress span { display: block; width: 100%; height: 100%; border-radius: inherit; background: #efbd59; transform-origin: left; animation-name: carousel-progress; animation-timing-function: linear; animation-fill-mode: forwards; }
.feature-carousel.paused .carousel-progress span { animation-play-state: paused; }
.carousel-dots { position: absolute; z-index: 5; right: 55px; top: 12px; display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 5px; max-width: 55%; }
.carousel-dots button { width: 7px; height: 7px; padding: 0; border: 0; border-radius: 50%; background: rgba(246,223,185,.45); cursor: pointer; }
.carousel-dots button.active { width: 19px; border-radius: 999px; background: #efbd59; }
.carousel-empty { display: grid; place-items: center; align-content: center; gap: 8px; min-height: 0; border: 1px dashed rgba(38,51,31,.36); border-radius: 22px; color: var(--afro-olive); text-align: center; }
.carousel-empty strong { font-family: var(--afro-text-serif); font-size: clamp(28px, 3vw, 44px); }
.carousel-empty span { font-weight: 900; text-transform: uppercase; letter-spacing: .08em; }
.feature-mobile-list { display: none; }
.feature-slide-enter-active, .feature-slide-leave-active { transition: opacity .24s ease, transform .24s ease; }
.feature-slide-enter-from { opacity: 0; transform: translateX(24px); }
.feature-slide-leave-to { opacity: 0; transform: translateX(-24px); }
@keyframes carousel-progress { from { transform: scaleX(0); } to { transform: scaleX(1); } }
.headline {
  margin-top: clamp(74px, 9vh, 95px);
  font-weight: 900;
  letter-spacing: .08em;
  color: var(--afro-olive);
  font-size: clamp(14px, 1.7vw, 18px);
  line-height: 1.55;
}
.headline p { margin: 0 0 4px; }
.right-copy { text-align: center; color: #130f0b; }
.coffee-line { display: inline-flex; gap: 10px; align-items: center; margin-top: 15px; }
.coffee-line i { width: 40px; height: 2px; background: var(--afro-ink); display: block; }
.coffee-line b { width: 16px; height: 22px; border-radius: 50%; background: var(--afro-ink); display: block; transform: rotate(25deg); }
.menu-tools {
  display: none;
  align-items: end;
  justify-content: space-between;
  gap: 14px;
  margin: 0 0 8px;
}
.share-mobile { display: none; }
.poster-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) 1px minmax(0, 1fr);
  gap: clamp(24px, 2.8vw, 32px);
  align-items: start;
  flex: 0 0 auto;
  min-height: 0;
  overflow: visible;
}
.full-width-menu { display: grid; gap: 12px; margin: 12px 0; flex: 0 0 auto; }
.full-width-category { padding: clamp(13px, 1.3vh, 18px); }
.full-width-category + .full-width-category { border-top: 1px dashed rgba(25,32,18,.5); }
.full-width-category :deep(.simple-list) { margin-top: 10px; }
.full-width-category :deep(.menu-row) { font-size: clamp(16px, .95vh, 18px); line-height: 1.25; }
.divider { width: 1px; align-self: stretch; background: rgba(148, 94, 29, .62); }
.left-stack,
.right-stack {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: clamp(16px, 1vh, 20px);
  min-height: 0;
}
.crepes-extras-row {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(170px, 1fr);
  gap: clamp(10px, 1.3vw, 16px);
  align-items: start;
  margin: clamp(11px, .9vh, 17px) 0;
  flex: 0 0 auto;
  min-height: 0;
}
.crepes-extras-row > :only-child { grid-column: 1 / -1; }
.extras-card { min-width: 0; padding: clamp(16px, 1.05vh, 21px) clamp(14px, 1.2vw, 18px); }
.extras-card .flag-label { width: min(175px, 78%); font-size: clamp(17px, 1vh, 20px); min-height: 40px; padding: 8px 25px 8px 14px; }
.extras-card .flag-label::after { border-top-width: 20px; border-bottom-width: 20px; border-left-width: 17px; right: -17px; }
.extras-list { margin-top: clamp(13px, .8vh, 16px); display: grid; gap: clamp(4px, .3vh, 6px); }
.extras-list :deep(.menu-row) { font-size: clamp(14px, .9vh, 17px); line-height: 1.3; gap: 6px; }
.feature-strip {
  margin: clamp(10px, 1.15vh, 16px) 0 clamp(8px, 1vh, 12px);
  padding: clamp(14px, 1.35vh, 20px);
  display: flex;
  flex-direction: column;
  gap: clamp(10px, 1.15vh, 16px);
  align-items: stretch;
  min-height: clamp(150px, 18vh, 230px);
  flex: 1 1 0;
  overflow: hidden;
}
.feature-heading {
  border: 1px dashed rgba(38, 51, 31, .35);
  border-radius: 16px;
  min-height: clamp(42px, 4.8vh, 58px);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(10px, 1.6vw, 22px);
  text-align: center;
  padding: 6px 18px;
  color: var(--afro-olive);
  flex: 0 0 auto;
}
.feature-heading span {
  font-family: var(--afro-text-serif);
  font-size: clamp(28px, 2.55vw, 40px);
  font-weight: 900;
  line-height: .95;
  letter-spacing: .02em;
}
.feature-heading small {
  font-weight: 900;
  font-size: clamp(12px, 1.05vw, 15px);
  line-height: 1.15;
  letter-spacing: .04em;
  text-transform: uppercase;
}
.feature-grid {
  min-height: 0;
  flex: 1 1 auto;
  display: grid;
  gap: clamp(10px, 1.15vh, 15px);
  grid-auto-rows: minmax(0, 1fr);
}
.feature-grid.count-1 { grid-template-columns: 1fr; }
.feature-grid.count-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.feature-grid.count-3-equal { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.feature-grid.count-3-mixed,
.feature-grid.count-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.feature-grid.count-5 { grid-template-columns: repeat(6, minmax(0, 1fr)); }
.feature-grid.count-5 .feature-card:nth-child(-n + 3) { grid-column: span 2; }
.feature-grid.count-5 .feature-card:nth-child(n + 4) { grid-column: span 3; }
.feature-grid.count-6 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.feature-grid.count-many { grid-template-columns: repeat(auto-fit, minmax(185px, 1fr)); }
.feature-card {
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(38,51,31,.94), rgba(61,77,48,.9));
  color: #f6dfb9;
  display: grid;
  grid-template-columns: minmax(100px, auto) 1fr;
  align-items: stretch;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.1), 0 10px 18px rgba(0,0,0,.12);
}
.feature-card.wide { grid-column: span 2; }
.feature-card:not(.has-photo) { grid-template-columns: 1fr; }
.feature-card img {
  width: clamp(105px, 10vw, 150px);
  height: 100%;
  min-height: 104px;
  object-fit: cover;
}
.feature-copy {
  padding: clamp(13px, 1.25vw, 19px);
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.feature-card:not(.has-photo) .feature-copy {
  text-align: center;
  align-items: center;
}
.feature-copy h3 {
  margin: 0;
  font-family: var(--afro-text-serif);
  font-size: clamp(22px, 2.05vw, 34px);
  line-height: 1.02;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.feature-copy p {
  margin: 8px 0 0;
  font-size: clamp(14px, 1.12vw, 18px);
  font-weight: 700;
  line-height: 1.24;
  color: rgba(246, 223, 185, .9);
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.feature-copy strong {
  margin-top: 9px;
  color: #f0bd55;
  font-size: clamp(20px, 1.75vw, 28px);
  line-height: 1;
  font-weight: 950;
}
.feature-grid.count-1 .feature-card {
  grid-template-columns: minmax(220px, .36fr) 1fr;
}
.feature-grid.count-1 .feature-card:not(.has-photo) { grid-template-columns: 1fr; }
.feature-grid.count-1 .feature-card img { width: 100%; }
.feature-grid.count-1 .feature-copy h3 { font-size: clamp(32px, 3.4vw, 52px); }
.feature-grid.count-1 .feature-copy p { font-size: clamp(18px, 1.45vw, 24px); -webkit-line-clamp: 3; }
.feature-grid.count-1 .feature-copy strong { font-size: clamp(28px, 2.55vw, 42px); }

@media (min-height: 900px) and (min-width: 901px) {
  .feature-grid.count-4 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: repeat(2, minmax(0, 1fr));
  }
}

.poster-footer {
  margin: auto -42px 0;
  min-height: clamp(56px, 6vh, 68px);
  background: linear-gradient(90deg, #10120f, #1b1c18);
  color: #d6a34b;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  align-items: center;
  gap: 0;
  text-transform: uppercase;
  letter-spacing: .08em;
  font-size: clamp(11px, 1vw, 13px);
  flex: 0 0 auto;
}
.poster-footer div { padding: 0 16px; border-right: 1px solid #d6a34b; min-height: 48px; display: flex; align-items: center; justify-content: center; gap: 12px; text-align: center; }
.poster-footer div:last-child { border-right: 0; }
.round-icon { width: clamp(32px, 3.2vw, 36px); height: clamp(32px, 3.2vw, 36px); border: 1px solid #d6a34b; border-radius: 50%; display: inline-grid; place-items: center; flex: 0 0 auto; }
.state-card { margin: 30px auto; padding: 22px; max-width: 500px; text-align: center; background: rgba(255,255,255,.35); border-radius: 18px; }
.state-card.error { color: #6b2f21; }
.search-results { min-height: 680px; padding-bottom: 35px; }
.results-head { display: flex; align-items: center; justify-content: space-between; gap: 18px; margin-bottom: 20px; }
.results-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.result-card { text-align: left; }
.result-card small { color: var(--afro-olive); font-weight: 900; text-transform: uppercase; letter-spacing: .12em; }
.result-card h3 { margin: 8px 0; font-family: var(--afro-text-serif); font-size: 26px; }
.price-pills { display: flex; gap: 8px; flex-wrap: wrap; }
.price-pills span { background: var(--afro-olive); color: #f3dfbd; border-radius: 999px; padding: 6px 10px; font-weight: 800; font-size: 13px; }
.price-pills .search-soldout { background: #b95f57; color: #fff8ef; text-transform: uppercase; letter-spacing: .05em; }
.empty-text { font-size: 18px; color: var(--afro-olive); }
.mobile-loyalty-cta { display: none; }
.mobile-share-qr { display: none; }
.mobile-whatsapp-float { display: none; }
.capture-toast {
  position: fixed;
  right: 18px;
  bottom: 18px;
  z-index: 9999;
  background: rgba(16, 18, 15, .92);
  color: #f3dfbd;
  border: 1px solid rgba(214, 163, 75, .65);
  border-radius: 999px;
  padding: 12px 18px;
  font-weight: 900;
  letter-spacing: .04em;
  box-shadow: 0 14px 30px rgba(0,0,0,.26);
}

/* En pantallas horizontales o bajas se prioriza lectura y se permite scroll. */
@media (max-height: 1650px) and (min-width: 901px) {
  .public-menu {
    height: auto;
    min-height: 100vh;
    align-items: flex-start;
    overflow: auto;
  }
  .menu-shell {
    height: auto;
    min-height: calc(100vh - clamp(12px, 2.3vh, 28px));
    overflow: hidden;
  }
  .poster-grid {
    flex: 0 0 auto;
    overflow: visible;
  }
  .left-stack,
  .right-stack {
    justify-content: flex-start;
    gap: 20px;
  }
  .crepes-extras-row {
    flex: 0 0 auto;
    margin: 20px 0;
  }
}

@media (max-width: 1100px) {
  .poster-footer { margin-left: -32px; margin-right: -32px; }
}
@media (max-width: 900px) {
  .public-menu { height: auto; min-height: 100vh; padding: 0; overflow: auto; }
  .menu-shell { border-radius: 0; height: auto; min-height: 100vh; padding: 16px 16px 0; overflow: hidden; }
  .menu-shell::before { inset: 5px; }
  .poster-head { grid-template-columns: 1fr; min-height: 0; text-align: center; margin-bottom: 14px; }
  .headline { margin: 0; }
  .left-copy,
  .right-copy { display: none; }
  .brand-mark { justify-content: center; }
  .brand-mark img { max-width: 330px; height: auto; max-height: none; }
  .feature-carousel, .carousel-empty, .header-qr-card { display: none; }
  .feature-mobile-list { display: grid; gap: 11px; margin-top: 4px; }
  .menu-tools { margin: 10px 0 22px; flex-direction: column; align-items: stretch; }
  .share-mobile { display: block; }
  .poster-grid { grid-template-columns: 1fr; gap: 22px; overflow: visible; }
  .left-stack,
  .right-stack { justify-content: flex-start; gap: 22px; }
  .divider { display: none; }
  .crepes-extras-row { grid-template-columns: 1fr; gap: 16px; margin: 22px 0; flex: 0 0 auto; min-height: 0; }
  .extras-card { padding: 15px; }
  .extras-card .flag-label { width: min(220px, 70%); font-size: 17px; }
  .extras-list { margin-top: 14px; gap: 5px; }
  .extras-list :deep(.menu-row) { font-size: 16px; line-height: 1.35; }
  .full-width-menu { margin: 22px 0; gap: 18px; }
  .full-width-category :deep(.menu-row) { font-size: 16px; line-height: 1.35; }
  .feature-strip {
    flex: 0 0 auto;
    min-height: 0;
    overflow: visible;
  }
  .feature-heading {
    flex-direction: column;
    gap: 3px;
    min-height: 0;
  }
  .feature-grid,
  .feature-grid.count-1,
  .feature-grid.count-2,
  .feature-grid.count-3-equal,
  .feature-grid.count-3-mixed,
  .feature-grid.count-4,
  .feature-grid.count-5,
  .feature-grid.count-6,
  .feature-grid.count-many { grid-template-columns: 1fr; }
  .feature-grid.count-5 .feature-card:nth-child(-n + 3),
  .feature-grid.count-5 .feature-card:nth-child(n + 4),
  .feature-card.wide { grid-column: auto; }
  .feature-card,
  .feature-grid.count-1 .feature-card { grid-template-columns: 104px 1fr; }
  .feature-card:not(.has-photo),
  .feature-grid.count-1 .feature-card:not(.has-photo) { grid-template-columns: 1fr; }
  .feature-card img,
  .feature-grid.count-1 .feature-card img { width: 104px; }
  .feature-copy { padding: 12px; }
  .feature-copy h3,
  .feature-grid.count-1 .feature-copy h3 { font-size: clamp(20px, 5.4vw, 28px); line-height: 1.05; }
  .feature-copy p,
  .feature-grid.count-1 .feature-copy p { font-size: clamp(12px, 3.55vw, 14px); line-height: 1.22; -webkit-line-clamp: 4; }
  .feature-copy strong,
  .feature-grid.count-1 .feature-copy strong { font-size: clamp(19px, 5vw, 24px); }
  .poster-footer { margin-left: -16px; margin-right: -16px; grid-template-columns: 1fr 1fr; padding: 12px 0; }
  .mobile-loyalty-cta { display: grid; gap: 12px; margin: 16px -2px 22px; padding: 16px; border-radius: 22px; background: rgba(38,51,31,.08); text-align: center; color: var(--afro-olive); }
  .mobile-loyalty-cta strong { font-size: 14px; line-height: 1.35; }
  .mobile-loyalty-cta a { display: inline-grid; place-items: center; min-height: 48px; border-radius: 999px; background: #26331f; color: #f3dfbd; text-decoration: none; font-weight: 1000; letter-spacing: .02em; }
  .mobile-share-qr { display: grid; grid-template-columns: minmax(132px, 42%) 1fr; align-items: center; gap: 16px; margin: 0 -2px 26px; padding: 16px; border: 1px solid rgba(214,163,75,.46); border-radius: 22px; background: linear-gradient(145deg, #344329, #24311e); color: #f3dfbd; text-align: left; }
  .mobile-share-qr img { width: 100%; padding: 7px; border-radius: 8px; background: #f3dfbd; }
  .mobile-share-qr div { display: grid; gap: 7px; }
  .mobile-share-qr .menu-qr-url { font-size: 12px; }
  .mobile-share-qr strong { color: #efbd59; font-family: var(--afro-text-serif); font-size: clamp(20px, 5.7vw, 27px); line-height: 1; }
  .mobile-share-qr span { font-size: 12px; font-weight: 800; line-height: 1.35; }
  .mobile-whatsapp-float { position: fixed; right: 16px; bottom: calc(18px + env(safe-area-inset-bottom)); z-index: 60; display: grid; place-items: center; width: 58px; height: 58px; border-radius: 50%; background: #25d366; color: #fff; text-decoration: none; box-shadow: 0 18px 36px rgba(0,0,0,.26); }
  .wa-icon { width: 30px; height: 30px; display: grid; place-items: center; } .wa-icon svg { width: 30px; height: 30px; display: block; }
  .wa-tooltip { position: absolute; right: 68px; bottom: 8px; width: min(235px, 72vw); background: rgba(17, 19, 15, .94); color: #f3dfbd; border: 1px solid rgba(214,163,75,.55); border-radius: 16px; padding: 10px 12px; font-size: 12px; line-height: 1.3; font-weight: 900; text-align: left; }
  .poster-footer div { border: 0; font-size: 11px; }
  .results-grid { grid-template-columns: 1fr; }
}
@media (max-width: 520px) {
  .poster-footer { grid-template-columns: 1fr; }
  .poster-footer div { min-height: 36px; }
  .mobile-share-qr { grid-template-columns: 1fr; text-align: center; }
  .mobile-share-qr img { width: min(230px, 84%); margin: auto; }
}
.menu-wallet-float { position: fixed; right: 16px; bottom: calc(88px + env(safe-area-inset-bottom)); z-index: 60; display: grid; place-items: center; width: 58px; height: 58px; border: 2px solid #f5dfb7; border-radius: 50%; background: #26331f; color: #fff8e9; text-decoration: none; box-shadow: 0 12px 28px #0004; transition: transform .15s; }.menu-wallet-float svg { width: 31px; height: 31px; }.menu-wallet-float:hover { transform: translateY(-2px); }.menu-wallet-float:focus-visible { outline: 3px solid #b68228; outline-offset: 3px; }
@media (min-width: 761px) { .menu-wallet-float { bottom: 22px; } }
@media (prefers-reduced-motion: reduce) { .menu-wallet-float { transition: none; } }
</style>
